import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Calendar,
  CheckCircle2,
  FileText,
  Filter,
  Mail,
  Phone,
  Plus,
  Search,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { useCandidates } from "@/hooks/useStaffing";
import CandidateCreateModal from "@/components/admin/recruitment/CandidateCreateModal";

export default function CandidatesListPage() {
  const [searchParams] = useSearchParams();
  const filterInvitation = searchParams.get("has_active_invitation") === "true";

  const { candidates, loading, error, fetchCandidates } = useCandidates();
  const [search, setSearch] = useState("");
  const [poolFilter, setPoolFilter] = useState("all"); // "all" | "in_pool" | "none"
  const [showCreateModal, setShowCreateModal] = useState(false);

  const filtered = useMemo(() => {
    return candidates.filter((c) => {
      if (filterInvitation && !c.has_active_invitation && !c.cv_review_requested) {
        return false;
      }
      if (poolFilter === "in_pool" && c.pool_status !== "in_pool" && c.pool?.status !== "in_pool") {
        return false;
      }
      if (poolFilter === "none" && (c.pool_status === "in_pool" || c.pool?.status === "in_pool")) {
        return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        const nameMatch = `${c.first_name} ${c.last_name}`.toLowerCase().includes(q);
        const emailMatch = (c.email || "").toLowerCase().includes(q);
        const roleMatch = (c.title || c.current_role || "").toLowerCase().includes(q);
        const skillsMatch = (c.skills || c.technologies || []).some((s) =>
          String(s).toLowerCase().includes(q),
        );
        return nameMatch || emailMatch || roleMatch || skillsMatch;
      }
      return true;
    });
  }, [candidates, search, poolFilter, filterInvitation]);

  return (
    <div className="space-y-6 pb-16" data-testid="candidates-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-white tracking-tight">
            Candidats
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Base centralisée des talents, cycles documentaires et vivier.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-md hover:bg-blue-500 transition-all self-start sm:self-auto"
          data-testid="create-candidate-button"
        >
          <UserPlus className="h-4 w-4" />
          Nouveau candidat
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, métier, technologie..."
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/80 border border-white/10 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
            data-testid="candidates-search-input"
          />
        </div>

        {/* Pool Filter Pills */}
        <div className="flex items-center gap-1.5">
          {[
            { id: "all", label: "Tous les profils" },
            { id: "in_pool", label: "Dans le vivier" },
            { id: "none", label: "Hors vivier" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setPoolFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                poolFilter === tab.id
                  ? "bg-blue-600 text-white font-semibold"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-rose-300 text-xs">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 glass rounded-2xl border border-white/10 text-slate-500">
          <Users className="h-10 w-10 mx-auto text-slate-600 mb-3" />
          <p className="text-base font-semibold text-slate-300">Aucun candidat trouvé</p>
          <p className="text-xs text-slate-500 mt-1">
            {search ? "Ajustez vos filtres de recherche." : "Ajoutez un premier profil candidat."}
          </p>
        </div>
      )}

      {/* Candidates List Cards */}
      {!loading && filtered.length > 0 && (
        <div className="grid gap-3.5">
          {filtered.map((cand) => {
            const isInPool = cand.pool_status === "in_pool" || cand.pool?.status === "in_pool";
            const candidateSkills = cand.skills || cand.technologies || [];
            const appsCount = cand.pipeline?.length || 0;

            return (
              <div
                key={cand._id}
                className="glass rounded-2xl p-5 border border-white/10 hover:border-white/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                data-testid={`candidate-card-${cand._id}`}
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="h-11 w-11 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-base shrink-0">
                    {(cand.first_name?.[0] || "C") + (cand.last_name?.[0] || "")}
                  </div>

                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/admin/recrutement/candidats/${cand._id}`}
                        className="font-bold text-white text-base hover:text-blue-300 transition-colors"
                      >
                        {cand.first_name} {cand.last_name}
                      </Link>
                      {cand.title && (
                        <span className="text-xs text-slate-300 font-medium truncate max-w-xs">
                          • {cand.title}
                        </span>
                      )}
                      {isInPool && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/20 flex items-center gap-1">
                          <Award className="h-3 w-3" />
                          Vivier
                        </span>
                      )}
                      {cand.status === "merged" && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-500/10 text-slate-400 border border-slate-500/20">
                          Fusionné
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3.5 w-3.5 text-slate-500" />
                        {cand.email}
                      </span>
                      {cand.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5 text-slate-500" />
                          {cand.phone}
                        </span>
                      )}
                      {cand.availability && (
                        <span className="text-slate-500">
                          Dispo : <span className="text-slate-300">{cand.availability}</span>
                        </span>
                      )}
                      <span className="text-slate-500">
                        Candidatures : <span className="text-blue-400 font-semibold">{appsCount}</span>
                      </span>
                    </div>

                    {/* Skills pills */}
                    {candidateSkills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {candidateSkills.slice(0, 5).map((skill, idx) => (
                          <span
                            key={idx}
                            className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700/60"
                          >
                            {skill}
                          </span>
                        ))}
                        {candidateSkills.length > 5 && (
                          <span className="text-[11px] px-1.5 py-0.5 text-slate-500">
                            +{candidateSkills.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                  <Link
                    to={`/admin/recrutement/candidats/${cand._id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-xl text-xs font-semibold transition-colors"
                    data-testid={`view-candidate-link-${cand._id}`}
                  >
                    Fiche & Cycles CV
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CandidateCreateModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCandidateCreated={fetchCandidates} />
    </div>
  );
}
